import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { BoardSummary, CreateTicketInput, FieldSet } from '../shared/index.js';
import {
  COST_CENTERS,
  COST_CENTER_LABELS,
  CURRENCIES,
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_LABELS,
  RECRUITMENT_LANGUAGES,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
} from '../shared/index.js';

import { ApiError } from '../api/client.js';
import type { DirectoryUser, JobRole } from '../api/endpoints.js';
import { attachmentApi, ticketApi } from '../api/endpoints.js';
import { queryKeys } from '../app/query-client.js';
import {
  useAssignableUsers,
  useDepartments,
  useDirectory,
  useJobRoles,
  useProjects,
} from '../hooks/use-directory.js';
import { Select } from './select.js';
import { Alert, Button, CONTROL, CONTROL_FIELD, CONTROL_SELECT } from './ui.js';

interface CreateTicketProps {
  readonly board: BoardSummary;
  readonly onClose: () => void;
  readonly onCreated: (ticketId: string) => void;
}

interface FormState {
  readonly title: string;
  readonly description: string;
  readonly priority: string;
  readonly departmentId: string;
  readonly assigneeId: string;
  readonly projectId: string;
  readonly costCenter: string;
  readonly estimatedAmount: string;
  readonly currency: string;
  readonly neededBy: string;
  readonly deliveryLocation: string;
  readonly projectNote: string;
  readonly position: string;
  readonly language: string;
  readonly deadline: string;
  readonly employmentType: string;
  readonly numberOfPeople: string;
  readonly emailAssignee: boolean;
}

const DESCRIPTION_TEMPLATES: Readonly<Record<FieldSet, string>> = {
  basic: `What needs to be done:


Why it is needed / context:


Deadline (if any):
`,
  purchase: `What I need to buy:


Where it should go (client site / HQ):


Why it is needed:


Estimated cost (if known):
`,
  recruitment: `Role / position to hire:


Language requirements:


Project / team:


Role details / must-haves:
`,
};

function blankForm(fieldSet: FieldSet): FormState {
  return {
    title: '',
    description: DESCRIPTION_TEMPLATES[fieldSet],
    priority: 'normal',
    departmentId: '',
    assigneeId: '',
    projectId: '',
    costCenter: 'hq',
    estimatedAmount: '',
    currency: 'EUR',
    neededBy: '',
    deliveryLocation: '',
    projectNote: '',
    position: '',
    language: 'English',
    deadline: '',
    employmentType: '',
    numberOfPeople: '1',
    emailAssignee: true,
  };
}

function lockedDepartmentIdFor(
  board: BoardSummary,
  jobRoles: readonly JobRole[],
  people: readonly DirectoryUser[],
): string | null {
  const target = board.intakeTarget;

  if (target === null) return null;
  if (target.kind === 'department') return target.departmentId;

  if (target.kind === 'position') {
    return jobRoles.find((entry) => entry.id === target.jobRoleId)?.departmentId ?? null;
  }

  return people.find((entry) => entry.id === target.userId)?.homeDepartmentId ?? null;
}

function buildPayload(board: BoardSummary, form: FormState): CreateTicketInput {
  const base = {
    boardId: board.id,
    title: form.title.trim(),
    description: form.description.trim() === '' ? null : form.description.trim(),
    priority: form.priority as CreateTicketInput['priority'],
    emailAssignee: form.emailAssignee,
    assigneeId: form.assigneeId === '' ? null : form.assigneeId,
  };

  if (board.fieldSet === 'purchase') {
    return {
      ...base,
      fieldSet: 'purchase',
      costCenter: form.costCenter as 'hq',
      currency: form.currency as 'EUR',
      estimatedAmount: form.estimatedAmount === '' ? null : Number(form.estimatedAmount),
      neededBy: form.neededBy === '' ? null : form.neededBy,
      deliveryLocation: form.deliveryLocation.trim() === '' ? null : form.deliveryLocation.trim(),
      projectNote: form.projectNote.trim() === '' ? null : form.projectNote.trim(),
      projectId: form.projectId === '' ? null : form.projectId,
    };
  }

  if (board.fieldSet === 'recruitment') {
    return {
      ...base,
      fieldSet: 'recruitment',
      position: form.position.trim(),
      language: form.language as 'English',
      deadline: new Date(form.deadline).toISOString(),
      projectId: form.projectId,
      employmentType: form.employmentType === '' ? null : (form.employmentType as 'full_time'),
      numberOfPeople: form.numberOfPeople === '' ? 1 : Number(form.numberOfPeople),
    };
  }

  return {
    ...base,
    fieldSet: 'basic',
    neededBy: form.neededBy === '' ? null : form.neededBy,
    departmentId: form.departmentId === '' ? null : form.departmentId,
    projectId: form.projectId === '' ? null : form.projectId,
  };
}

export function CreateTicket({ board, onClose, onCreated }: CreateTicketProps): ReactNode {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => blankForm(board.fieldSet));
  const [problem, setProblem] = useState<string | null>(null);
  const [files, setFiles] = useState<readonly File[]>([]);
  const [raised, setRaised] = useState<{
    readonly id: string;
    readonly failed: readonly string[];
  } | null>(null);

  const { departments } = useDepartments();
  const projects = useProjects();
  const { jobRoles } = useJobRoles();
  const people = useDirectory();

  const assignable = useAssignableUsers(
    board.intakeTarget === null
      ? form.departmentId === ''
        ? null
        : form.departmentId
      : (lockedDepartmentIdFor(board, jobRoles, people) ?? null),
  );

  const patch = (part: Partial<FormState>): void => {
    setForm((current) => ({ ...current, ...part }));
  };

  const create = useMutation({
    mutationFn: async () => {
      const ticket = await ticketApi.create(buildPayload(board, form));

      const failed: string[] = [];

      for (const file of files) {
        try {
          await attachmentApi.upload(ticket.id, file);
        } catch {
          failed.push(file.name);
        }
      }

      return { ticket, failed };
    },
    onSuccess: async ({ ticket, failed }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.boards.cards(board.id) });

      if (failed.length === 0) {
        onCreated(ticket.id);
        return;
      }

      setRaised({ id: ticket.id, failed });
      setProblem(
        `The request was raised, but ${failed.length === 1 ? 'this file' : 'these files'} could not be attached: ${failed.join(', ')}. You can add ${failed.length === 1 ? 'it' : 'them'} from the request itself.`,
      );
    },
    onError: (error: unknown) => {
      setProblem(
        error instanceof ApiError
          ? (error.issues[0]?.message ?? error.message)
          : 'The request could not be raised.',
      );
    },
  });

  const target = board.intakeTarget;

  const targetPerson =
    target?.kind === 'person' ? people.find((entry) => entry.id === target.userId) : undefined;

  const targetJobRoleId =
    target?.kind === 'position' ? target.jobRoleId : (targetPerson?.jobRoleId ?? null);

  const targetRole = jobRoles.find((entry) => entry.id === targetJobRoleId);

  const lockedDepartmentId =
    target === null
      ? null
      : target.kind === 'department'
        ? target.departmentId
        : (targetRole?.departmentId ?? targetPerson?.homeDepartmentId ?? null);

  const lockedDepartmentName =
    departments.find((entry) => entry.id === lockedDepartmentId)?.name ?? null;

  const lockedAssigneeName =
    targetPerson === undefined ? null : `${targetPerson.firstName} ${targetPerson.lastName}`;

  const routedChoices = assignable.filter(
    (person) => targetJobRoleId === null || person.jobRoleId === targetJobRoleId,
  );

  const picksDepartment = target === null && board.fieldSet === 'basic';
  const showsAssignee = target !== null || picksDepartment;

  const mayCreate =
    form.title.trim() !== '' && (board.fieldSet !== 'recruitment' || form.projectId !== '');

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-stretch bg-navy-deep/45 backdrop-blur-sm sm:place-items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`New request on ${board.name}`}
    >
      <div className="h-dvh w-full overflow-y-auto bg-white shadow-menu sm:h-auto sm:max-h-[88dvh] sm:max-w-[560px] sm:rounded-card dark:bg-dark-panel">
        <div className="h-1 rounded-t-card bg-linear-to-r from-gold via-cyan to-royal" />

        <form
          className="p-5 sm:p-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (raised !== null) return;
            setProblem(null);
            create.mutate();
          }}
        >
          <h2 className="font-display text-[22px] font-extrabold tracking-[-0.02em] text-navy dark:text-dark-ink">
            New request on {board.name}
          </h2>
          <p className="mt-1 mb-5 text-[13.5px] text-muted">
            {lockedDepartmentName === null
              ? 'Pick the department that should handle it.'
              : lockedAssigneeName === null
                ? `This board sends it to ${lockedDepartmentName}. Name who should pick it up, or leave that to the board.`
                : `This board sends it straight to ${lockedAssigneeName}.`}
          </p>

          {problem === null ? null : <Alert tone="error">{problem}</Alert>}

          <label className="field-label" htmlFor="ticket-title">
            Title
          </label>
          <input
            id="ticket-title"
            required
            value={form.title}
            onChange={(event) => {
              patch({ title: event.target.value });
            }}
            className={`${CONTROL_FIELD} mb-4`}
          />

          <label className="field-label" htmlFor="ticket-description">
            Description
          </label>
          <textarea
            id="ticket-description"
            rows={4}
            value={form.description}
            onChange={(event) => {
              patch({ description: event.target.value });
            }}
            placeholder="What is needed, and why"
            className={`${CONTROL} mb-4`}
          />

          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="ticket-priority">
                Priority
              </label>
              <Select
                id="ticket-priority"
                label="Priority"
                className={CONTROL_SELECT}
                value={form.priority}
                options={TICKET_PRIORITIES.map((priority) => ({
                  value: priority,
                  label: TICKET_PRIORITY_LABELS[priority],
                }))}
                onChange={(next) => {
                  patch({ priority: next });
                }}
              />
            </div>

            <div>
              <label className="field-label" htmlFor="ticket-project">
                Project {board.fieldSet === 'recruitment' ? '' : '(optional)'}
              </label>
              <Select
                id="ticket-project"
                label="Project"
                className={CONTROL_SELECT}
                value={form.projectId}
                options={[
                  {
                    value: '',
                    label: board.fieldSet === 'recruitment' ? 'Choose a project…' : 'None',
                  },
                  ...projects.map((project) => ({ value: project.id, label: project.name })),
                ]}
                onChange={(next) => {
                  patch({ projectId: next });
                }}
              />
            </div>
          </div>

          {board.fieldSet === 'basic' ? (
            <div className="mb-4">
              <label className="field-label" htmlFor="ticket-basic-needed-by">
                Needed by (optional)
              </label>
              <input
                id="ticket-basic-needed-by"
                type="date"
                value={form.neededBy}
                onChange={(event) => {
                  patch({ neededBy: event.target.value });
                }}
                className={CONTROL_FIELD}
              />
            </div>
          ) : null}

          {showsAssignee ? (
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="ticket-department">
                  Department
                </label>
                {lockedDepartmentId === null ? (
                  <Select
                    id="ticket-department"
                    label="Department"
                    className={CONTROL_SELECT}
                    value={form.departmentId}
                    options={[
                      { value: '', label: 'Choose one' },
                      ...departments.map((department) => ({
                        value: department.id,
                        label: department.name,
                      })),
                    ]}
                    onChange={(next) => {
                      patch({ departmentId: next, assigneeId: '' });
                    }}
                  />
                ) : (
                  <div className={`${CONTROL_SELECT} flex items-center text-muted`}>
                    {lockedDepartmentName ?? 'Set by this board'}
                  </div>
                )}
              </div>

              <div>
                <label className="field-label" htmlFor="ticket-assignee">
                  {lockedAssigneeName === null ? 'Assignee (optional)' : 'Assignee'}
                </label>
                {lockedAssigneeName === null ? (
                  <Select
                    id="ticket-assignee"
                    label="Assignee"
                    className={CONTROL_SELECT}
                    value={form.assigneeId}
                    options={[
                      { value: '', label: 'Leave to the board' },
                      ...routedChoices.map((person) => ({
                        value: person.id,
                        label: `${person.firstName} ${person.lastName}`,
                      })),
                    ]}
                    onChange={(next) => {
                      patch({ assigneeId: next });
                    }}
                  />
                ) : (
                  <div className={`${CONTROL_SELECT} flex items-center text-muted`}>
                    {lockedAssigneeName}
                  </div>
                )}

                {lockedAssigneeName !== null || targetJobRoleId === null ? null : (
                  <p className="mt-1.5 text-[12px] text-muted">
                    This board routes to {targetRole?.name ?? 'a position'}, so only the people
                    holding it are offered.
                  </p>
                )}
              </div>
            </div>
          ) : null}

          {board.fieldSet === 'purchase' ? (
            <>
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="ticket-cost-center">
                    Cost center
                  </label>
                  <Select
                    id="ticket-cost-center"
                    label="Cost center"
                    className={CONTROL_SELECT}
                    value={form.costCenter}
                    options={COST_CENTERS.map((center) => ({
                      value: center,
                      label: COST_CENTER_LABELS[center],
                    }))}
                    onChange={(next) => {
                      patch({ costCenter: next });
                    }}
                  />
                </div>

                <div>
                  <label className="field-label" htmlFor="ticket-needed-by">
                    Needed by
                  </label>
                  <input
                    id="ticket-needed-by"
                    type="date"
                    value={form.neededBy}
                    onChange={(event) => {
                      patch({ neededBy: event.target.value });
                    }}
                    className={CONTROL_FIELD}
                  />
                </div>
              </div>

              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="ticket-amount">
                    Estimated amount
                  </label>
                  <input
                    id="ticket-amount"
                    type="number"
                    min="0"
                    value={form.estimatedAmount}
                    onChange={(event) => {
                      patch({ estimatedAmount: event.target.value });
                    }}
                    className={CONTROL_FIELD}
                  />
                </div>

                <div>
                  <label className="field-label" htmlFor="ticket-currency">
                    Currency
                  </label>
                  <Select
                    id="ticket-currency"
                    label="Currency"
                    className={CONTROL_SELECT}
                    value={form.currency}
                    options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
                    onChange={(next) => {
                      patch({ currency: next });
                    }}
                  />
                </div>
              </div>

              <label className="field-label" htmlFor="ticket-delivery-location">
                Delivery location
              </label>
              <input
                id="ticket-delivery-location"
                value={form.deliveryLocation}
                onChange={(event) => {
                  patch({ deliveryLocation: event.target.value });
                }}
                placeholder="Client site, HQ office, person name, address"
                className={`${CONTROL_FIELD} mb-4`}
              />

              <label className="field-label" htmlFor="ticket-project-note">
                Client / project note
              </label>
              <input
                id="ticket-project-note"
                value={form.projectNote}
                onChange={(event) => {
                  patch({ projectNote: event.target.value });
                }}
                placeholder="Use this when picking from the project list is not enough"
                className={`${CONTROL_FIELD} mb-4`}
              />
            </>
          ) : null}

          {board.fieldSet === 'recruitment' ? (
            <>
              <label className="field-label" htmlFor="ticket-position">
                Position
              </label>
              <input
                id="ticket-position"
                required
                value={form.position}
                onChange={(event) => {
                  patch({ position: event.target.value });
                }}
                className={`${CONTROL_FIELD} mb-4`}
              />

              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="ticket-language">
                    Working language
                  </label>
                  <Select
                    id="ticket-language"
                    label="Working language"
                    className={CONTROL_SELECT}
                    value={form.language}
                    options={RECRUITMENT_LANGUAGES.map((language) => ({
                      value: language,
                      label: language,
                    }))}
                    onChange={(next) => {
                      patch({ language: next });
                    }}
                  />
                </div>

                <div>
                  <label className="field-label" htmlFor="ticket-deadline">
                    Deadline
                  </label>
                  <input
                    id="ticket-deadline"
                    type="date"
                    required
                    value={form.deadline}
                    onChange={(event) => {
                      patch({ deadline: event.target.value });
                    }}
                    className={CONTROL_FIELD}
                  />
                </div>
              </div>

              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="ticket-employment-type">
                    Employment type
                  </label>
                  <Select
                    id="ticket-employment-type"
                    label="Employment type"
                    className={CONTROL_SELECT}
                    value={form.employmentType}
                    options={[
                      { value: '', label: 'Not specified' },
                      ...EMPLOYMENT_TYPES.map((type) => ({
                        value: type,
                        label: EMPLOYMENT_TYPE_LABELS[type],
                      })),
                    ]}
                    onChange={(next) => {
                      patch({ employmentType: next });
                    }}
                  />
                </div>

                <div>
                  <label className="field-label" htmlFor="ticket-number-of-people">
                    Number of people
                  </label>
                  <input
                    id="ticket-number-of-people"
                    type="number"
                    min="1"
                    max="999"
                    value={form.numberOfPeople}
                    onChange={(event) => {
                      patch({ numberOfPeople: event.target.value });
                    }}
                    className={CONTROL_FIELD}
                  />
                </div>
              </div>
            </>
          ) : null}

          <div className="mb-4">
            <label className="field-label" htmlFor="ticket-files">
              Attachments
            </label>
            <input
              id="ticket-files"
              type="file"
              multiple
              onChange={(event) => {
                setFiles(Array.from(event.target.files ?? []));
              }}
              className="w-full text-[13px] text-muted file:mr-3 file:rounded-field file:border-0 file:bg-mist file:px-3 file:py-1.5 file:text-[13px] file:font-semibold file:text-navy dark:file:bg-dark-field dark:file:text-dark-ink"
            />
            {files.length === 0 ? null : (
              <ul className="mt-2 flex flex-col gap-1">
                {files.map((file) => (
                  <li key={file.name} className="text-[12.5px] text-muted">
                    {file.name} ({Math.ceil(file.size / 1024)} kB)
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="mt-1 flex items-start gap-2.5 text-[13.5px] text-navy dark:text-dark-ink">
            <input
              type="checkbox"
              checked={form.emailAssignee}
              onChange={(event) => {
                patch({ emailAssignee: event.target.checked });
              }}
              className="mt-0.5"
            />
            <span>
              Also email the assignee
              <span className="block text-[12.5px] text-muted">
                The description is always saved on the request either way.
              </span>
            </span>
          </label>

          <div className="mt-6 flex justify-end gap-2">
            {raised === null ? (
              <>
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" loading={create.isPending} disabled={!mayCreate}>
                  Raise the request
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={() => {
                  onCreated(raised.id);
                }}
              >
                Open the request
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
